import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import type { SocialPostSceneBlockV1 } from "@runcheetah/shared";

import { CreativeColorPicker } from "@/components/campaign/creative-color-picker";
import {
  CreativeHashtagInput,
  CreativeInspectorLabel as Label,
  CreativeInspectorNumberField as InspectorNumberField,
} from "@/components/campaign/creative-layer-editor-controls";
import { commitNumericValue } from "@/components/campaign/creative-layer-editor-utils";
import {
  formatImageFitOption,
  getImageFitOption,
  IMAGE_FIT_OPTIONS,
  type ImageFitValue,
} from "@/lib/image-fit-options";
import { Input } from "@runcheetah/ui";
import type { IntlShape } from "react-intl";

export function CreativeInspectorAdvancedGeometry({
  block,
  title,
  labels,
  minSize,
  onUpdate,
}: {
  block: SocialPostSceneBlockV1;
  title: string;
  labels: { x: string; y: string; width: string; height: string };
  minSize?: number;
  onUpdate: (changes: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-2">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-[10px] font-semibold uppercase tracking-widest text-foreground/40">
          {title}
        </span>
        <ChevronDown
          className={[
            "h-3 w-3 text-foreground/35 transition-transform duration-150",
            open ? "rotate-180" : "",
          ].join(" ")}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="grid min-w-0 gap-2 pt-2.5 sm:grid-cols-2">
          <InspectorNumberField
            label={labels.x}
            value={block.x}
            onCommit={(x) => onUpdate({ x })}
          />
          <InspectorNumberField
            label={labels.y}
            value={block.y}
            onCommit={(y) => onUpdate({ y })}
          />
          <InspectorNumberField
            label={labels.width}
            value={block.width}
            min={minSize}
            onCommit={(width) => onUpdate({ width })}
          />
          <InspectorNumberField
            label={labels.height}
            value={block.height}
            min={minSize}
            onCommit={(height) => onUpdate({ height })}
          />
        </div>
      ) : null}
    </div>
  );
}

export function CreativeInspectorColorField({
  label,
  value,
  clearLabel,
  colorPickerLabels,
  onCommit,
  allowClear = false,
  brandColors = [],
}: {
  label: string;
  value: string;
  clearLabel: string;
  colorPickerLabels: {
    brandPalette: string;
    hexInput: string;
    hueSlider: string;
    openPicker: string;
    useBrandColor: (color: string) => string;
  };
  onCommit: (value: string) => void;
  allowClear?: boolean;
  brandColors?: string[];
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      {label ? (
        <div className="flex items-center justify-between gap-3">
          <Label>{label}</Label>
          {allowClear ? (
            <button
              type="button"
              onClick={() => onCommit("")}
              className="text-xs text-foreground/45 underline underline-offset-2"
            >
              {clearLabel}
            </button>
          ) : null}
        </div>
      ) : null}
      <CreativeColorPicker
        value={value}
        brandColors={brandColors}
        brandPaletteLabel={colorPickerLabels.brandPalette}
        hexInputLabel={colorPickerLabels.hexInput}
        hueSliderLabel={colorPickerLabels.hueSlider}
        openPickerLabel={colorPickerLabels.openPicker}
        useBrandColorLabel={colorPickerLabels.useBrandColor}
        onCommit={onCommit}
      />
    </div>
  );
}

export function CreativeInspectorFitField({
  intl,
  label,
  value,
  onChange,
}: {
  intl: IntlShape;
  label: ReactNode;
  value: ImageFitValue;
  onChange: (value: ImageFitValue) => void;
}) {
  const selectedOption = formatImageFitOption(intl, getImageFitOption(value));
  const options = IMAGE_FIT_OPTIONS.map((option) => formatImageFitOption(intl, option));

  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        <span className="text-[11px] font-medium text-foreground/45">{selectedOption.label}</span>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={[
              "min-w-0 rounded-lg border px-2 py-2 text-center text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
              value === option.value
                ? "border-foreground/20 bg-muted text-foreground"
                : "border-transparent text-foreground/58 hover:bg-muted/45 hover:text-foreground/75",
            ].join(" ")}
            aria-label={option.ariaLabel}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            <span className="block truncate">{option.label}</span>
          </button>
        ))}
      </div>
      <p className="px-1 text-xs leading-5 text-foreground/55">{selectedOption.detail}</p>
    </div>
  );
}

export function CreativeInspectorCornerRadiusField({
  label,
  customLabel,
  optionLabels,
  value,
  maxRoundRadius,
  onChange,
}: {
  label: ReactNode;
  customLabel: string;
  optionLabels: { square: string; soft: string; round: string };
  value: number;
  maxRoundRadius: number;
  onChange: (value: number) => void;
}) {
  const maxCustomRadius = Math.min(maxRoundRadius, 100);
  const [draftValue, setDraftValue] = useState(String(Math.round(value)));
  const options = [
    { label: optionLabels.square, radius: 0, previewClassName: "rounded-none" },
    { label: optionLabels.soft, radius: 16, previewClassName: "rounded-md" },
    { label: optionLabels.round, radius: maxCustomRadius, previewClassName: "rounded-full" },
  ];

  useEffect(() => {
    setDraftValue(String(Math.round(value)));
  }, [value]);

  const commitDraftValue = () => {
    commitNumericValue({
      value: draftValue,
      fallback: value,
      min: 0,
      max: maxCustomRadius,
      onCommit: onChange,
      reset: setDraftValue,
    });
  };

  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        <div className="flex items-center gap-1 text-foreground/45">
          <Input
            type="number"
            value={draftValue}
            min={0}
            max={maxCustomRadius}
            step={1}
            aria-label={customLabel}
            onChange={(event) => setDraftValue(event.target.value)}
            onBlur={commitDraftValue}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                commitDraftValue();
              }
            }}
            className="h-6 w-14 rounded-md border-border/45 bg-transparent px-1.5 text-right text-xs tabular-nums text-foreground/55 shadow-none focus-visible:ring-1 focus-visible:ring-foreground/15"
          />
          <span className="text-xs">px</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {options.map((option) => (
          <button
            key={option.label}
            type="button"
            className={[
              "flex min-w-0 flex-col items-center gap-1.5 rounded-xl border px-2 py-2 text-xs transition-colors",
              value === option.radius
                ? "border-accent/55 bg-accent/10 text-foreground"
                : "border-input bg-background text-foreground/58 hover:bg-muted/45",
            ].join(" ")}
            aria-pressed={value === option.radius}
            onClick={() => onChange(option.radius)}
          >
            <span
              className={`h-8 w-8 border border-foreground/35 bg-foreground/10 ${option.previewClassName}`}
              aria-hidden="true"
            />
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CreativePostHashtagInput({
  hashtags,
  placeholder,
  addLabel,
  onChange,
}: {
  hashtags: string[];
  placeholder: string;
  addLabel: ReactNode;
  onChange: (hashtags: string[]) => void;
}) {
  return (
    <CreativeHashtagInput
      hashtags={hashtags}
      placeholder={placeholder}
      addLabel={addLabel}
      onChange={onChange}
    />
  );
}
